import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Stack,
  Card,
  CardHeader,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import { CheckStatusChip } from "../../../components/CheckStatusChip/CheckStatusChip";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";
import DeactivatedIntegrationsAlert from "../../../components/DeactivatedIntegrationsAlert/DeactivatedIntegrationsAlert";
import FilterBar from "../../../components/FilterNSearchBar/FilterNSearchBar";
import ScopeActionButton from "../../../components/ScopeActionButton/ScopeActionButton";
import AccessControl, { FEATURE_IDS } from "../../../components/AccessControl";
import { IntegrationKey, statusFilterOptions } from "../../../utils/enums";
import {
  GET_REPOS,
  GET_CODE_SECURITY_SUMMARY,
  SET_REPOS_SCOPE,
  SET_CODE_SECURITY_SCORED_LEVELS,
  TRIGGER_CODE_SECURITY_SCAN,
  GENERATE_CODE_SECURITY_EXCEL,
} from "../../../apis/organization-apis/CodeSecurity-apis/CodeSecurityApis";
import {
  SCOPE_TOGGLE_SX,
  TABLE_MIN_WIDTH_SX,
  FILTER_BAR_WRAP_SX,
  CARD_HEADER_SX,
  matchesFilter,
  includesQuery,
  countLabel,
  primaryButtonSx,
  outlinedButtonSx,
  SECRETS_FOUND_FILTER_OPTIONS,
  secretsFoundValue,
  hasActiveFilters,
  useCodeSecurityListFilters,
  CODE_SECURITY_PATH,
  compareStatusFailingFirst,
} from "./codeSecurityShared";
import { useCheckRunStatusPolling } from "../../Checks/hooks/useCheckRunStatusPolling";


const VCS_INTEGRATION_KEYS = [IntegrationKey.GITHUB, IntegrationKey.BITBUCKET];

const LEVEL_OPTIONS = [
  {
    value: "confirmed",
    label: "Confirmed secrets",
    hint: "Always on. We are sure this is a password, API key, or token.",
  },
  {
    value: "unconfirmed",
    label: "Possible secrets",
    hint: "Looks like a secret, but we are not sure yet.",
  },
];

const STATS_CARD_SX = {
  p: 1.5,
  borderRadius: "12px",
  border: "1px solid #E8EAED",
  backgroundColor: "#FFFFFF",
  flex: 1,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    transform: "translateY(-2px)",
  },
};

const levelColor = (level) => {
  if (level === "confirmed") return "error";
  if (level === "unconfirmed") return "warning";
  return "default";
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString() : "—";

const CodeSecurityHome = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    tab,
    searchQuery,
    tableFilters,
    setTab,
    setSearchQuery,
    setTableFilters,
    clearFilters,
  } = useCodeSecurityListFilters();
  const [snackbar, setSnackbar] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [scanInFlight, setScanInFlight] = useState(false);
  const lastScannedSnapshot = useRef(null);
  const inScope = tab === "in";
  const scanWatching = scanInFlight;

  const {
    data: summaryData,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(GET_CODE_SECURITY_SUMMARY, {
    fetchPolicy: "network-only",
    pollInterval: scanWatching ? 10000 : 0,
  });
  const summary = summaryData?.getCodeSecuritySummary?.data;

  const {
    data: reposData,
    loading: reposLoading,
    refetch: refetchRepos,
  } = useQuery(GET_REPOS, {
    fetchPolicy: "network-only",
    pollInterval: scanWatching ? 10000 : 0,
  });

  const allRepos = reposData?.getRepos?.data || [];
  const inScopeRepos = useMemo(
    () => allRepos.filter((row) => row.in_scope === true),
    [allRepos]
  );
  const outScopeRepos = useMemo(
    () => allRepos.filter((row) => row.in_scope !== true),
    [allRepos]
  );
  const repos = inScope ? inScopeRepos : outScopeRepos;
  const tableLoading = reposLoading;

  const [setScoredLevels, { loading: levelsLoading }] = useMutation(
    SET_CODE_SECURITY_SCORED_LEVELS
  );
  const [setReposScope, { loading: bulkScopeLoading }] = useMutation(
    SET_REPOS_SCOPE
  );
  const [triggerScan, { loading: scanLoading }] = useMutation(
    TRIGGER_CODE_SECURITY_SCAN
  );
  const [generateCodeSecurityExcel, { loading: exportLoading }] = useMutation(
    GENERATE_CODE_SECURITY_EXCEL
  );

  const scoredLevels = summary?.scored_levels || ["confirmed"];
  const notConnected = summary?.connected === false;
  const hasData =
    inScopeRepos.length > 0 ||
    outScopeRepos.length > 0 ||
    [
      summary?.repos_in_scope,
      summary?.branches_in_scope,
      summary?.total_findings,
      summary?.scored_findings,
    ].some((count) => (count || 0) > 0);
  const stillLoading = summaryLoading || reposLoading;
  const showConnectEmptyState = notConnected && !hasData && !stillLoading;

  const columns = useMemo(
    () => [
      { label: "", field: "select", width: "5%", align: "center" },
      { label: "Repository", field: "repo", width: "30%", align: "left" },
      { label: "Monitored branches", field: "branches", width: "18%", align: "left" },
      {
        label: "Secrets found",
        field: "findings",
        width: "22%",
        align: "left",
        filterConfig: { filterOptions: SECRETS_FOUND_FILTER_OPTIONS },
      },
      {
        label: "Status",
        field: "status",
        width: "16%",
        align: "left",
        filterConfig: { filterOptions: statusFilterOptions },
      },
      { label: "Actions", field: "action", width: "12%", align: "center" },
    ],
    []
  );

  const filteredRepos = useMemo(
    () =>
      repos
        .filter((row) => {
          const haystack = `${row.owner || ""}/${row.repo || ""}`;
          return (
            includesQuery(searchQuery, haystack, row.owner, row.repo, row.default_branch) &&
            matchesFilter(tableFilters.status, row.status) &&
            matchesFilter(
              tableFilters.findings,
              secretsFoundValue(row.level_counts, scoredLevels)
            )
          );
        })
        .sort((a, b) => {
          const byStatus = compareStatusFailingFirst(a, b);
          if (byStatus !== 0) return byStatus;
          return `${a.owner || ""}/${a.repo || ""}`.localeCompare(
            `${b.owner || ""}/${b.repo || ""}`
          );
        }),
    [repos, searchQuery, tableFilters, scoredLevels]
  );

  const refetchAll = useCallback(
    () => Promise.all([refetchRepos(), refetchSummary()]),
    [refetchRepos, refetchSummary]
  );

  const { isPollingRunStatus, startPolling } = useCheckRunStatusPolling(
    summary?.check_id,
    refetchAll
  );

  const scanBusy = scanLoading || scanInFlight || isPollingRunStatus;

  useEffect(() => {
    if (!isPollingRunStatus) return;
    if (lastScannedSnapshot.current == null) {
      lastScannedSnapshot.current = summary?.last_scanned_at || null;
    }
    setScanInFlight(true);
  }, [isPollingRunStatus, summary?.last_scanned_at]);

  useEffect(() => {
    if (!scanInFlight) return;
    const current = summary?.last_scanned_at || null;
    if (current && current !== lastScannedSnapshot.current) {
      setScanInFlight(false);
      setSnackbar({
        message: "Scan finished.",
        variant: "success",
      });
    }
  }, [scanInFlight, summary?.last_scanned_at]);

  useEffect(() => {
    if (!scanInFlight) return undefined;
    const timeoutId = setTimeout(() => setScanInFlight(false), 15 * 60 * 1000);
    return () => clearTimeout(timeoutId);
  }, [scanInFlight]);

  const handleLevels = async (nextLevels, { silent, successMessage } = {}) => {
    const next = Array.from(new Set(["confirmed", ...nextLevels]));
    const result = await setScoredLevels({
      variables: { input: { scored_levels: next } },
    });
    const payload = result?.data?.setCodeSecurityScoredLevels;
    if (!silent) {
      setSnackbar({
        message: payload?.success
          ? successMessage || "Saved."
          : payload?.message || "Could not save that setting.",
        variant: payload?.success ? "success" : "error",
      });
    }
    await refetchAll();
  };

  const toggleLevel = (value) => {
    if (value === "confirmed") return;
    const turningOn = !scoredLevels.includes(value);
    const next = turningOn
      ? [...scoredLevels, value]
      : scoredLevels.filter((level) => level !== value);
    handleLevels(next, {
      successMessage: turningOn
        ? "Possible secrets will now fail the check."
        : "The check will only fail for confirmed secrets.",
    });
  };

  useEffect(() => {
    if (summaryLoading || !summary) return;
    if ((summary.scored_levels || []).includes("confirmed")) return;
    handleLevels(summary.scored_levels || [], { silent: true });
  }, [summaryLoading, summary]);

  const handleScan = async () => {
    try {
      const result = await triggerScan();
      const payload = result?.data?.triggerCodeSecurityScan;
      if (payload?.success) {
        lastScannedSnapshot.current = summary?.last_scanned_at || null;
        setScanInFlight(true);
        startPolling();
        setSnackbar({
          message:
            "We're scanning in-scope repositories. Results will show here when the scan finishes.",
          variant: "success",
        });
      } else {
        setSnackbar({
          message: payload?.message || "Could not start the scan.",
          variant: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        message: err?.message || "Could not start the scan.",
        variant: "error",
      });
    }
  };

  const handleExportToExcel = async () => {
    try {
      const { data } = await generateCodeSecurityExcel();
      const url = data?.generateCodeSecurityExcel?.url;
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setSnackbar({
          message: "Export failed: no download URL returned.",
          variant: "error",
        });
      }
    } catch (err) {
      setSnackbar({
        message: err?.message || "Export failed.",
        variant: "error",
      });
    }
  };

  const handleSelectRow = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    const allVisibleSelected =
      filteredRepos.length > 0 &&
      filteredRepos.every((row) => selectedIds.has(String(row.id)));
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredRepos.map((row) => String(row.id))));
  };

  const handleTabChange = (_, value) => {
    if (value === null) return;
    setTab(value);
    setSelectedIds(new Set());
  };

  const handleBulkScope = async (next) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    const result = await setReposScope({
      variables: { repo_data_ids: ids, in_scope: next },
    });
    const payload = result?.data?.setReposScope;
    setSnackbar({
      message: payload?.message || "Updated",
      variant: payload?.success ? "success" : "error",
    });
    if (payload?.success) setSelectedIds(new Set());
    await refetchAll();
  };

  const renderHeaderCell = (column) => {
    if (column?.field !== "select") return null;
    const allSelected =
      filteredRepos.length > 0 &&
      filteredRepos.every((row) => selectedIds.has(String(row.id)));
    const someSelected =
      !allSelected &&
      filteredRepos.some((row) => selectedIds.has(String(row.id)));
    return (
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected}
        onChange={handleSelectAll}
        sx={{ p: 0 }}
      />
    );
  };

  const renderCell = (row, column) => {
    switch (column.field) {
      case "select":
        return (
          <Checkbox
            size="small"
            checked={selectedIds.has(String(row.id))}
            onChange={() => handleSelectRow(row.id)}
            onClick={(event) => event.stopPropagation()}
            sx={{ p: 0 }}
          />
        );
      case "repo":
        return (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.owner}/{row.repo}
            </Typography>
            {row.default_branch && (
              <Typography variant="caption" color="text.secondary">
                default: {row.default_branch}
              </Typography>
            )}
          </Box>
        );
      case "branches": {
        const monitored = row.in_scope_branch_count || 0;
        const total = row.branch_count || 0;
        const allMonitored = total > 0 && monitored === total;
        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              py: 0.75,
              px: 1.5,
              border: `1px dashed ${allMonitored
                  ? theme.customColors.status.success.text
                  : theme.customColors.border.main
                }`,
              borderRadius: 1.5,
              backgroundColor: allMonitored
                ? theme.customColors.status.success.bg
                : theme.customColors.background.light,
            }}
          >
            <Typography
              variant="body2"
              color={
                allMonitored
                  ? theme.customColors.status.success.text
                  : theme.customColors.text.strong
              }
              sx={{ lineHeight: 1.4 }}
            >
              {monitored}/{total}
            </Typography>
          </Box>
        );
      }
      case "findings": {
        const visibleCounts = (row.level_counts || []).filter((lc) =>
          scoredLevels.includes(lc.level)
        );
        return visibleCounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {visibleCounts.map((lc) => (
              <Chip
                key={lc.level}
                size="small"
                variant="outlined"
                color={levelColor(lc.level)}
                label={`${lc.level === "confirmed" ? "Confirmed" : "Possible"
                  }: ${lc.count}`}
              />
            ))}
          </Box>
        );
      }
      case "status":
        return <CheckStatusChip status={row.status} />;
      case "action":
        return (
          <Tooltip title="View details" arrow>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`${CODE_SECURITY_PATH}/${row.id}`);
              }}
              sx={{
                color: theme.customColors.icons.dark,
                "&:hover": {
                  backgroundColor: theme.customColors.background.light,
                  transform: "translateX(2px)",
                  transition: "all 0.2s ease",
                },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        );
      default:
        return row[column.field];
    }
  };

  const StatCard = ({ value, label, icon, valueColor }) => (
    <Paper elevation={0} sx={STATS_CARD_SX}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight="600"
            color={valueColor || theme.customColors.text.strong}
          >
            {summaryLoading ? "—" : value}
          </Typography>
          <Typography variant="body2" color={theme.customColors.text.secondary}>
            {label}
          </Typography>
        </Box>
        {icon}
      </Box>
    </Paper>
  );

  if (showConnectEmptyState) {
    return (
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5, maxWidth: 720 }}
        >
          Keep your source code secure. Connect a provider so we can monitor
          the repositories you put in scope.
        </Typography>
        <Box
          sx={{
            mt: "12vh",
            mx: "auto",
            width: { xs: "90%", sm: "60%", md: "48%" },
            textAlign: "center",
            py: 8,
            px: 4,
            backgroundColor: theme.customColors.background.main,
            borderRadius: "12px",
            border: `1px solid ${theme.customColors.border.main}`,
            boxShadow: theme.customColors.shadow.card,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Connect a code provider
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Connect GitHub or Bitbucket so we can monitor the security of your
            repositories.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/integrations?domain=vcs_provider")}
          >
            Go to Integrations
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {notConnected && (
        <Box sx={{ mb: 2 }}>
          <DeactivatedIntegrationsAlert
            integrationKeys={VCS_INTEGRATION_KEYS}
            onManageIntegrations={() =>
              navigate("/integrations?domain=vcs_provider")
            }
            dismissalStorageKey="regodit_deactivated_dismiss_code_security"
          />
        </Box>
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1.5, maxWidth: 720 }}
      >
        Keep your source code secure. Choose which repositories to monitor
        from GitHub, Bitbucket, or any version-controlled source, so we can
        scan for vulnerabilities in your source code.
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page is your overview of code security. Put repositories in scope, run scans, and track whether checks are passing."
          steps={[
            "Connect **GitHub, Bitbucket, or any version-controlled source** from Integrations so we can see your repositories.",
            "Use **In Scope** for the repositories we should scan. Everything else stays on **Out of Scope** and is ignored.",
            "Select repositories and use **Add to scans** or **Remove from scans** in the table header to change several at once.",
            "Tick **Possible secrets** if the check should also fail when we are not sure yet. **Confirmed secrets always fail the check.**",
            "Click **Scan now** to run a fresh scan of everything in scope.",
          ]}
          footerText="💡 We never store the real secret — only a redacted preview."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <StatCard
          value={summary?.repos_in_scope ?? 0}
          label="Repos being scanned"
          icon={
            <AccountTreeOutlinedIcon
              sx={{ color: theme.customColors.icons.primary, fontSize: 24 }}
            />
          }
        />
        <StatCard
          value={summary?.scored_findings ?? 0}
          label="Secrets found"
          valueColor={
            (summary?.scored_findings || 0) > 0
              ? theme.customColors.status.warning.text
              : theme.customColors.text.strong
          }
          icon={
            <VpnKeyOutlinedIcon
              sx={{ color: theme.customColors.icons.primary, fontSize: 24 }}
            />
          }
        />
        <Paper elevation={0} sx={STATS_CARD_SX}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box sx={{ mb: 0 }}>
                <CheckStatusChip
                  status={summary?.check_status}
                  isRefreshing={scanBusy}
                />
              </Box>
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
              >
                Check status
              </Typography>
            </Box>
            {summary?.check_type && (
              <Tooltip title="View Details" arrow>
                <IconButton
                  size="small"
                  onClick={() =>
                    window.open(`/checks/${summary.check_type}`, "_blank")
                  }
                  sx={{
                    color: theme.customColors.icons.dark,
                    "&:hover": {
                      backgroundColor: theme.customColors.background.light,
                      transform: "translateX(2px)",
                      transition: "all 0.2s ease",
                    },
                  }}
                >
                  <OpenInNewRoundedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>
      </Stack>

      <Paper
        elevation={0}
        sx={{ ...STATS_CARD_SX, mb: 1.5, flex: "none", py: 1.25, px: 2 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-start" }}
          spacing={2}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ mt: 0.75, whiteSpace: "nowrap" }}
          >
            Fail the check for:
          </Typography>
          {LEVEL_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              disabled={levelsLoading || opt.value === "confirmed"}
              sx={{ alignItems: "flex-start", mr: 2, ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={
                    opt.value === "confirmed" ||
                    scoredLevels.includes(opt.value)
                  }
                  onChange={() => toggleLevel(opt.value)}
                  sx={{ pt: 0.5 }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {opt.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.hint}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      </Paper>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors.text.strong}
              >
                Repositories
              </Typography>
              <ToggleButtonGroup
                value={tab}
                exclusive
                onChange={handleTabChange}
                aria-label="repository scope"
                size="small"
                sx={SCOPE_TOGGLE_SX}
              >
                <ToggleButton value="in">In Scope</ToggleButton>
                <ToggleButton value="out">Out of Scope</ToggleButton>
              </ToggleButtonGroup>
              {hasActiveFilters(searchQuery, tableFilters) && (
                <Chip
                  label="Filtered"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    height: 24,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.customColors.text.secondary,
                    borderColor: theme.customColors.border.main,
                    background: theme.customColors.background.light,
                  }}
                />
              )}
            </Box>
          }
          action={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {selectedIds.size > 0 ? (
                <>
                  <ScopeActionButton
                    mode={inScope ? "out" : "in"}
                    label={inScope ? "Remove from scans" : "Add to scans"}
                    disabled={bulkScopeLoading}
                    onClick={() => handleBulkScope(!inScope)}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedIds(new Set())}
                    sx={outlinedButtonSx(theme)}
                  >
                    Clear
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {scanBusy
                      ? "Scan running…"
                      : `Last scanned: ${formatDate(summary?.last_scanned_at)}`}
                  </Typography>
                  <AccessControl
                    featureId={FEATURE_IDS.CODE_SECURITY_EXCEL_EXPORT}
                  >
                    <Tooltip
                      title="Export repositories, branches, and findings to Excel"
                      arrow
                    >
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={
                            exportLoading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            )
                          }
                          onClick={handleExportToExcel}
                          disabled={allRepos.length === 0 || exportLoading}
                          sx={outlinedButtonSx(theme)}
                        >
                          Export
                        </Button>
                      </span>
                    </Tooltip>
                  </AccessControl>
                  <Button
                    variant="contained"
                    startIcon={
                      scanBusy ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrowIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    disabled={scanBusy}
                    onClick={handleScan}
                    sx={primaryButtonSx(theme)}
                  >
                    {scanBusy ? "Scanning…" : "Scan now"}
                  </Button>
                </>
              )}
            </Box>
          }
          sx={CARD_HEADER_SX}
        />
        <CardContent sx={{ p: 0 }}>
          <Box sx={FILTER_BAR_WRAP_SX}>
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(event) => setSearchQuery(event.target.value)}
              filters={[]}
              selectedFilter={null}
              onFilterChange={() => { }}
              onClearFilters={clearFilters}
              countLabel={countLabel(
                filteredRepos.length,
                "repository",
                "repositories"
              )}
            />
          </Box>
          <Divider sx={{ borderColor: "#E8EAED" }} />
          <Box sx={TABLE_MIN_WIDTH_SX}>
            <ReusableTable
              columns={columns}
              data={filteredRepos}
              loading={tableLoading || levelsLoading}
              renderCell={renderCell}
              renderHeaderCell={renderHeaderCell}
              onFilterChange={setTableFilters}
              filters={tableFilters}
              height="auto"
              emptyState={
                <Typography variant="body1" color="text.secondary">
                  {repos.length === 0
                    ? inScope
                      ? "Nothing is being monitored yet. Open Out of Scope and pick a repository."
                      : "No repositories out of scope."
                    : "No repositories match your search."}
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      {snackbar && (
        <ConfirmSnackbar
          snackbarInfo={snackbar}
          setSnackbarInfo={setSnackbar}
        />
      )}
    </Box>
  );
};

export default CodeSecurityHome;
